import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { TCAppConfig } from "../../../../../shared/app-configs/tc-app-configs";
import { InnerButton } from "../../../../../shared/components/buttons/floating-button-container";
import { LoaderComponent } from "../../../../../shared/components/loaders/page-loader";
import CustomTable from "../../../../../shared/components/table/custom-table";
import { useApi } from "../../../../../shared/hooks/useApi";
import useNotification from "../../../../../shared/hooks/useNotification";
import { ErrorHandler } from "../../../../../shared/utils/error-handler.utils";
import {
  CertifiedWeightClaimsValidationResult,
  certifiedWeightClaimFromRow,
  certifiedWeightClaimsValidationError,
  certifiedWeightRowsFromResponseData,
  validateCertifiedWeightClaims,
} from "./certified-products/product-details/claims-category.utils";

interface RowData {
  tcClaimsCategory: string;
  tcClaimsCategoryCertifiedWeight: string;
}

interface CertifiedWeightState {
  rows: any[];
  displayRows: RowData[];
  validation: CertifiedWeightClaimsValidationResult | null;
}

const certifiedWeightFromRow = (row: any): number | string =>
  row?.tcClaimsCategoryCertifiedWeight ??
  row?.tcProductCertifiedWeightSum ??
  0;

const mapCertifiedWeightRows = (rows: any[]): RowData[] =>
  rows.map((row) => ({
    tcClaimsCategory: certifiedWeightClaimFromRow(row),
    tcClaimsCategoryCertifiedWeight: String(certifiedWeightFromRow(row)),
  }));

const VERIFY_ERROR =
  "Certified-weight Claims Categories could not be loaded. Please retry before continuing.";

const CERTIFIED_WEIGHT_COLUMNS = [
  {
    accessorKey: "tcClaimsCategory",
    header: "TC Claims Category",
  },
  {
    accessorKey: "tcClaimsCategoryCertifiedWeight",
    header: "TC Claims Category Certified Weight",
  },
];

const TcStandardWeightInfo: React.FC<{
  onNextTab: () => Promise<void>;
}> = ({ onNextTab }) => {
  const [data, setData] = useState<RowData[]>([]);
  const [validationMessage, setValidationMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveInFlight = useRef(false);
  const { TE_ID, formtype } = useParams<string>();
  const { loading, executeGet, executePost } = useApi();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const hasSubmitTc = formtype === "submited-tc";

  const loadCertifiedWeightState = useCallback(
    async (validateRows: boolean): Promise<CertifiedWeightState> => {
      if (!TE_ID) {
        throw new Error("TC ID is required");
      }

      const certifiedWeightResponse = await executeGet(
        TCAppConfig.GET_CERTIFIED_WEIGHT_INFO_CONSOLIDATED_V4,
        { tcId: TE_ID }
      );

      if (certifiedWeightResponse?.status !== 200) {
        throw new Error("Certified-weight information could not be loaded");
      }

      const rows = certifiedWeightRowsFromResponseData(
        certifiedWeightResponse.data
      );
      const validation = validateRows
        ? validateCertifiedWeightClaims(rows)
        : null;

      return {
        rows,
        displayRows: mapCertifiedWeightRows(rows),
        validation,
      };
    },
    [TE_ID, executeGet]
  );

  useEffect(() => {
    let active = true;

    if (!TE_ID) {
      setData([]);
      setValidationMessage(VERIFY_ERROR);
      return undefined;
    }

    setIsVerifying(true);
    loadCertifiedWeightState(!hasSubmitTc)
      .then(({ displayRows, validation }) => {
        if (!active) return;
        setData(displayRows);
        setValidationMessage(
          validation ? certifiedWeightClaimsValidationError(validation) : ""
        );
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching certified weight info:", error);
        setData([]);
        setValidationMessage(VERIFY_ERROR);
      })
      .finally(() => {
        if (active) setIsVerifying(false);
      });

    return () => {
      active = false;
    };
  }, [TE_ID, hasSubmitTc, loadCertifiedWeightState]);

  const declarationPath = `/v4/tc/${
    formtype || "create-tc"
  }/declaration-submit-details/${TE_ID}`;

  const handleNext = async () => {
    if (hasSubmitTc) {
      navigate(declarationPath);
      return;
    }
    if (!TE_ID) {
      notify("TC ID is required", "error");
      return;
    }
    if (saveInFlight.current) {
      return;
    }

    saveInFlight.current = true;
    setIsSaving(true);
    try {
      const { rows, displayRows, validation } =
        await loadCertifiedWeightState(true);
      setData(displayRows);

      const currentValidationMessage = validation
        ? certifiedWeightClaimsValidationError(validation)
        : VERIFY_ERROR;
      setValidationMessage(currentValidationMessage);
      if (!validation?.isValid) {
        notify(currentValidationMessage, "error");
        return;
      }

      const postBody = {
        tcId: TE_ID,
        tcClaimsCategoryCertifiedWeightInformation: rows.map((row) => ({
          tcClaimsCategory: certifiedWeightClaimFromRow(row),
          tcClaimsCategoryCertifiedWeight: certifiedWeightFromRow(row),
        })),
      };
      const result = await executePost(
        TCAppConfig.POST_CERTIFIED_WEIGHT_INFO_V4,
        postBody
      );
      if (result?.status !== 200) {
        notify(
          ErrorHandler.extractErrorMessage(
            result,
            "Unable to save certified-weight information"
          ),
          "error"
        );
        return;
      }

      await onNextTab();
      navigate(declarationPath);
    } catch (error) {
      console.error("Error verifying certified weight info:", error);
      setValidationMessage(VERIFY_ERROR);
      notify(VERIFY_ERROR, "error");
    } finally {
      saveInFlight.current = false;
      setIsSaving(false);
    }
  };

  return (
    <>
      {loading || isVerifying ? (
        <LoaderComponent />
      ) : (
        <div>
          <Typography fontWeight="600">
            TC Claims Category Certified Weight Details Summary
          </Typography>

          {validationMessage && !hasSubmitTc && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationMessage}
            </Alert>
          )}

          <Box sx={{ width: "100%", textAlign: "left", mb: 3, mt: 2 }}>
            <CustomTable
              columns={CERTIFIED_WEIGHT_COLUMNS}
              data={data}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableRowActions={false}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: "0.5rem",
            }}
          >
            <NavLink to="/v4/tc-dashboard">
              <InnerButton>Home</InnerButton>
            </NavLink>
            <NavLink
              to={`/v4/tc/${
                formtype || "create-tc"
              }/incoming-tc-details/${TE_ID}`}
            >
              <InnerButton>Back</InnerButton>
            </NavLink>
            {data.length > 0 && (
              <InnerButton
                onClick={handleNext}
                disabled={isSaving || isVerifying}
              >
                {isSaving ? "Saving..." : "Next"}
              </InnerButton>
            )}
          </Box>
        </div>
      )}
    </>
  );
};

export default TcStandardWeightInfo;
